import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutGrid,
  Store,
  ClipboardList,
  Users,
  Wallet,
  BarChart3,
  BookOpen,
  GraduationCap,
  Settings as SettingsIcon,
  ChevronDown,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProBranding } from '@/lib/pro-branding';
import { useProCourseProgress } from '@/hooks/use-pro-formation';

interface NavItem {
  to?: string;
  label: string;
  icon: typeof LayoutGrid;
  children?: { to: string; label: string }[];
  /** Pastille de fin de ligne — la progression de la formation, ici. */
  badge?: string;
}

export function ElectricianSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  const location = useLocation();
  const { loginLogoUrl } = useProBranding();
  const course = useProCourseProgress();

  const items: NavItem[] = [
    // Le tableau de bord ouvre l'espace et garde l'icône historique de la
    // grille ; la boutique passe en second avec son propre pictogramme.
    { to: '/tableau-de-bord', label: t('pro.sidebar.dashboard', 'Tableau de bord'), icon: LayoutGrid },
    { to: '/home', label: t('pro.sidebar.shop', 'Boutique'), icon: Store },
    {
      label: t('pro.sidebar.orders', 'Mes commandes'),
      icon: ClipboardList,
      children: [
        { to: '/devis', label: t('pro.sidebar.devis', 'Devis') },
        { to: '/ordres', label: t('pro.sidebar.ordres', 'Ordres') },
      ],
    },
    { to: '/catalogue', label: t('pro.sidebar.catalogue', 'Catalogue'), icon: BookOpen },
    { to: '/clients', label: t('pro.sidebar.clients', 'Clients'), icon: Users },
    { to: '/portefeuille', label: t('pro.sidebar.portefeuille', 'Portefeuille'), icon: Wallet },
    { to: '/statistique', label: t('pro.sidebar.statistique', 'Statistique'), icon: BarChart3 },
    // La formation ne s'affiche que si le fournisseur a publié un parcours.
    ...(course.hasCourse
      ? [
          {
            to: '/formation',
            label: t('pro.sidebar.formation', 'Formation'),
            icon: GraduationCap,
            badge: course.isComplete
              ? undefined
              : `${course.completedLessons}/${course.totalLessons}`,
          } as NavItem,
        ]
      : []),
    { to: '/settings', label: t('pro.sidebar.settings', 'Paramètres'), icon: SettingsIcon },
  ];

  // Auto-expand "Mes commandes" if current route is devis or ordres
  const isOrdersActive =
    location.pathname.startsWith('/devis') || location.pathname.startsWith('/ordres');
  const [ordersOpen, setOrdersOpen] = useState(isOrdersActive);

  return (
    <aside
      className="w-[221px] shrink-0 h-full bg-white flex flex-col"
      style={{ borderRight: '1px solid var(--pro-line)' }}
    >
      {/* Logo */}
      <div className="px-5 py-6">
        {loginLogoUrl ? (
          <img
            src={loginLogoUrl}
            alt="Tunielec Pro"
            className="max-h-10 max-w-[160px] object-contain"
          />
        ) : (
          <div className="flex items-baseline gap-1">
            <span
              className="text-[20px] font-bold leading-none"
              style={{ fontFamily: 'Figtree', color: 'var(--pro-text-dark)' }}
            >
              Tunelec
            </span>
            <span
              className="text-[20px] font-semibold italic leading-none"
              style={{ fontFamily: 'Figtree', color: 'var(--pro-primary)' }}
            >
              Pro
            </span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-0.5">
        {items.map((item) => {
          const Icon = item.icon;
          if (item.children) {
            return (
              <div key={item.label}>
                <button
                  type="button"
                  onClick={() => setOrdersOpen((v) => !v)}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-[13px] font-medium transition-colors"
                  style={{
                    color: isOrdersActive ? 'var(--pro-primary)' : 'var(--pro-text-grey)',
                    background: isOrdersActive ? 'var(--pro-primary-subtle)' : 'transparent',
                    fontFamily: 'Figtree',
                  }}
                >
                  <span className="flex items-center gap-2.5">
                    <Icon className="w-[18px] h-[18px]" />
                    {item.label}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${ordersOpen ? 'rotate-180' : ''}`}
                    style={{ color: 'var(--pro-text-light)' }}
                  />
                </button>
                <div className="pro-accordion" data-open={ordersOpen}>
                  <div className="pro-accordion-content">
                    <div className="ml-9 mt-1 space-y-0.5">
                      {item.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          onClick={onNavigate}
                          end
                          className="block px-3 py-2 rounded-lg text-[13px] font-medium transition-colors"
                          style={({ isActive }) => ({
                            fontFamily: 'Figtree',
                            color: isActive ? 'var(--pro-primary)' : 'var(--pro-text-grey)',
                            background: isActive ? 'var(--pro-primary-subtle)' : 'transparent',
                          })}
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={item.to!}
              onClick={onNavigate}
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-colors"
              style={({ isActive }) => ({
                fontFamily: 'Figtree',
                color: isActive ? 'var(--pro-primary)' : 'var(--pro-text-grey)',
                background: isActive ? 'var(--pro-primary-subtle)' : 'transparent',
              })}
            >
              <Icon className="w-[18px] h-[18px]" />
              <span className="flex-1">{item.label}</span>
              {item.badge && (
                <span
                  className="shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums"
                  style={{
                    background: 'var(--pro-primary)',
                    color: 'white',
                  }}
                >
                  {item.badge}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* WhatsApp contact button */}
      <div className="p-3">
        <a
          href="https://wa.me/21600000000"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2.5 px-3 py-2.5 rounded-3xl text-white text-[13px] font-medium hover:opacity-90 transition-opacity"
          style={{ background: '#25D366' }}
        >
          <span
            className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
            style={{ background: 'rgba(255,255,255,0.2)' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
            </svg>
          </span>
          {t('pro.sidebar.contact', 'Contactez-nous')}
        </a>
      </div>
    </aside>
  );
}
